import { unfocus } from '@/_utils/document/unfocus';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { LoaderService } from '@/common/loader/loader.service';
import { NavbarModule } from '@/common/navbar/navbar.module';
import { TransportApiService } from '@/new-transport/_api/transport.api.service';
import {
	GroupedVehicleDataMap,
	TransportKind,
	TransportModule,
	TransportSide,
	VehicleData,
} from '@/new-transport/_models/transport.model';
import {
	TransportCopyDialogComponent,
	TransportCopyDialogData,
} from '@/new-transport/_shared/topbar/transport-copy-dialog/transport-copy-dialog.component';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnDestroy, OnInit, output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { toSignal } from '@angular/core/rxjs-interop';
import { OfferTransportKind } from '@/common/enums/OfferTransportKind';
import { catchError, EMPTY, filter, finalize, map, Observable, pipe, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { UnlinkTransportConfirmDialogComponent } from '@/new-transport/_shared/unlink-transport-dialog/unlink-transport-confirm-dialog/unlink-transport-confirm-dialog.component';
import {
	UnlikDialogData,
	UnlinkTransportDialogComponent,
} from '@/new-transport/_shared/unlink-transport-dialog/unlink-transport-dialog/unlink-transport-dialog.component';
import { StatusCode } from '@/common/enums/StatusCode';
import { TransportModalEmailDialogComponent } from './transport-modal-email-dialog/transport-modal-email-dialog.component';

@Component({
	selector: 'app-top-bar',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [NavbarModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule],
	templateUrl: './top-bar.component.html',
	styleUrl: './top-bar.component.scss',
})
export class TopBarComponent implements OnInit, OnDestroy {
	readonly module = input.required<TransportModule>();
	readonly transportName = input('');
	readonly transportId = input(null);
	readonly transportsToLinkNames = input<string[]>([]);
	readonly side = input<TransportSide>(null);
	readonly buttonsDisabled = input.required<boolean>();
	readonly kind = input<TransportKind>(null);
	readonly disableSaveButton = input<boolean>(false);
	readonly coachListUrl = input(null);

	readonly saveTransportMapEvent = output<void>();
	readonly saveTransportCoachListEvent = output<void>();
	readonly saveTransportTimetableEvent = output<void>();
	readonly changedTransportName = output<string>();
	readonly resetCoachListEvent = output<void>();
	readonly copyCoachListEvent = output<void>();

	readonly TransportSide = TransportSide;

	readonly transportSide = computed(() => {
		switch (this.side()) {
			case TransportSide.DEPARTURE:
				return '- Wyjazd';
			case TransportSide.RETURN:
				return '- Powrót';
			default:
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				//@ts-expect-error
				const value: never = this.side();
				throw new Error(`There is no option like ${value}`);
		}
	});

	readonly moduleName = computed(() => {
		switch (this.module()) {
			case TransportModule.MAP:
				return 'Mapa';
			case TransportModule.TIMETABLE:
				return 'Rozkład jazdy';
			case TransportModule.LIST:
				return 'Listy';
			case TransportModule.COACH_LIST:
				return 'Listy autokarowe';
			default:
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-expect-error
				const value: never = this.module();
				throw new Error(`There is no option like ${value}`);
		}
	});

	readonly title = computed(() => {
		const isPlane = this.queryParams()['transportKind'] === OfferTransportKind.PLANE;
		return `Transport (${this.moduleName()}) ${this.transportSide()} ${this.kind() === TransportKind.TEMPLATE ? ' (Szablon)' : ''} ${isPlane ? '<i class="fas fa-plane"></i>' : '<i class="fas fa-bus"></i>'}`;
	});

	readonly transportNameControl = new FormControl(this.transportName(), [Validators.required]);

	readonly TransportModule = TransportModule;

	private readonly updateTransportName = rxMethod<string>(
		pipe(
			tap((name: string) => {
				this.transportNameControl.patchValue(name);
			}),
		),
	);

	private readonly queryParams = toSignal(inject(ActivatedRoute).queryParams);
	private readonly router = inject(Router);
	private readonly activatedRoute = inject(ActivatedRoute);
	private readonly dialog = inject(MatDialog);
	private readonly transportApiService = inject(TransportApiService);
	private readonly infoboxService = inject(InfoboxService);
	private readonly loader = inject(LoaderService);

	private readonly destroy$ = new Subject<void>();

	ngOnInit(): void {
		this.updateTransportName(this.transportName);

		this.transportNameControl.valueChanges
			.pipe(takeUntil(this.destroy$))
			.subscribe((name) => this.changedTransportName.emit(name));
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	switchModule(module: TransportModule): void {
		const moduleUrlMap: Record<TransportModule, string> = {
			[TransportModule.MAP]: `transport/${this.transportId()}/map/new`,
			[TransportModule.TIMETABLE]: `transport/${this.transportId()}/table/new`,
			[TransportModule.LIST]: `/transport/${this.transportId()}/list`,
			[TransportModule.COACH_LIST]: `transport/${this.transportId()}/coach`,
		};

		const url = moduleUrlMap[module];

		if (!url) {
			this.infoboxService.error(`There is no option like ${module}`);
			return;
		}

		this.router.navigate([url], {
			queryParams: this.activatedRoute.snapshot.queryParams,
		});
	}

	async openTransportCopyModal(): Promise<void> {
		const transportCopyComponent = await import('./transport-copy-dialog/transport-copy-dialog.component').then(
			(c) => c.TransportCopyDialogComponent,
		);
		this.dialog.open<TransportCopyDialogComponent, TransportCopyDialogData>(transportCopyComponent, {
			data: { transportId: this.transportId() },
			width: '400px',
		});
	}

	saveTransport(): void {
		switch (this.module()) {
			case TransportModule.LIST:
				break;
			case TransportModule.TIMETABLE:
				this.saveTimetable();
				break;
			case TransportModule.MAP:
				this.saveTransportMap();
				break;
			case TransportModule.COACH_LIST:
				this.saveTransportCoachList();
				break;
			default:
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-expect-error
				const value: never = this.module();
				throw new Error(`There is no option like ${value}`);
		}
	}

	removeTransport(): void {
		this.loader.show();
		this.transportApiService
			.deleteTransport(this.transportId())
			.pipe(finalize(() => this.loader.hide()))
			.subscribe(() => {
				this.infoboxService.success('Pomyślnie usunięto transport!');
				this.router.navigate(['/transportTerm']);
			});
	}

	openMailDialog(): void {
		this.loader.show();
		this.transportApiService
			.getTransportPassengers(this.transportId())
			.pipe(
				finalize(() => this.loader.hide()),
				filter((vehiclesData) => !!vehiclesData),
				map((item) => this.groupByVehicle(item.passengersInLogic)),
			)
			.subscribe((vehiclesData: GroupedVehicleDataMap) => this.triggerMailDialog(vehiclesData));
	}

	unlinkTransport(): void {
		this.dialog
			.open(UnlinkTransportConfirmDialogComponent)
			.afterClosed()
			.pipe(
				filter((confirmed: boolean) => confirmed),
				switchMap(() =>
					this.transportApiService.unlinkTransport(this.transportId()).pipe(
						catchError((error) => {
							if (error.status === StatusCode.CONFLICT_RESPONSE_ERROR) {
								return this.triggerUnlinkForceDialog(error.error);
							} else {
								console.error('Unexpected error:', error);
								return EMPTY;
							}
						}),
					),
				),
			)
			.subscribe(() => {
				this.infoboxService.success('Transport został rozłączony');
				this.router.navigate(['/transportTerm']);
			});
	}

	resetCoachList(): void {
		this.resetCoachListEvent.emit();
	}

	copyCoachList(): void {
		this.copyCoachListEvent.emit();
	}

	async updateName(): Promise<void> {
		const component = await import('../../update-transport-name/update-transport-name.component').then(
			(c) => c.UpdateTransportNameComponent,
		);

		this.dialog
			.open(component, {
				data: { transportId: this.transportId(), transportName: this.transportNameControl.value },
				width: '50vw',
				maxWidth: '700px',
			})
			.afterClosed()
			.pipe(filter((value) => typeof value === 'string'))
			.subscribe((value) => {
				this.transportNameControl.patchValue(value);
				unfocus();
			});
	}

	private saveTransportMap(): void {
		this.saveTransportMapEvent.emit();
	}

	private saveTransportCoachList(): void {
		this.saveTransportCoachListEvent.emit();
	}

	private saveTimetable(): void {
		this.saveTransportTimetableEvent.emit();
	}

	private groupByVehicle(data: VehicleData[]): GroupedVehicleDataMap {
		const groupedVehicleData: GroupedVehicleDataMap = new Map<string, Omit<VehicleData, 'vehicle'>[]>();
		data.forEach((value) => {
			const { vehicle, ...rest } = value;
			if (groupedVehicleData.has(vehicle.name)) {
				groupedVehicleData.get(vehicle.name).push(rest);
			} else {
				groupedVehicleData.set(vehicle.name, [rest]);
			}
		});
		return groupedVehicleData;
	}

	private triggerMailDialog(vehiclesData: GroupedVehicleDataMap): void {
		this.dialog.open(TransportModalEmailDialogComponent, {
			height: '50%',
			data: vehiclesData,
		});
	}

	private triggerUnlinkForceDialog(dialogData: UnlikDialogData): Observable<void> {
		return this.dialog
			.open(UnlinkTransportDialogComponent, { data: dialogData })
			.afterClosed()
			.pipe(
				switchMap((confirmed: boolean) => {
					if (confirmed) {
						return this.transportApiService
							.unlinkTransport(this.transportId(), confirmed)
							.pipe(tap(() => console.log('Success: Forced unlink successful.')));
					} else {
						return EMPTY;
					}
				}),
			);
	}
}
